import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, X, ExternalLink, AlertTriangle, MapPin, CalendarDays } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CTA = { event: "Ver evento", business: "Ver negócio", restaurant: "Ver restaurante", job: "Ver emprego", classified: "Ver anúncio", product: "Ver produto", service: "Ver serviço" };
const LABEL = { event: "Evento", business: "Negócio", restaurant: "Restaurante", job: "Emprego", classified: "Anúncio", product: "Produto", service: "Serviço" };

export function ShareCard({ share }) {
  const s = share?.snapshot;
  if (!s) return null;
  if (share.available === false) {
    return (
      <div className="mt-2 rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-3 text-xs text-zinc-500 flex items-center gap-2" data-testid={`share-card-unavailable-${s.id}`}>
        <AlertTriangle size={14} className="shrink-0" /> Conteúdo indisponível — o {LABEL[s.type]?.toLowerCase() || "item"} original já não está disponível.
      </div>
    );
  }
  return (
    <Link to={s.path} className="mt-2 flex rounded-xl border border-zinc-200 overflow-hidden bg-zinc-50 hover:border-zinc-400 transition-colors" data-testid={`share-card-${s.id}`}>
      {s.image && <img src={s.image} alt="" className="w-24 h-24 object-cover shrink-0" loading="lazy" />}
      <div className="p-3 min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wide text-zinc-500 font-semibold">{LABEL[s.type]}</div>
        <div className="text-sm font-semibold leading-tight line-clamp-2" data-testid="share-card-title">{s.title}</div>
        {s.subtitle && <div className="text-xs text-zinc-600 line-clamp-1 mt-0.5">{s.subtitle}</div>}
        <div className="text-[11px] text-zinc-500 mt-1 flex flex-wrap gap-2 items-center">
          {s.price_label && <span className="font-bold text-[#c8102e]" data-testid="share-card-price">{s.price_label}</span>}
          {s.date && <span className="inline-flex items-center gap-0.5"><CalendarDays size={10} /> {s.date}</span>}
          {s.city && <span className="inline-flex items-center gap-0.5"><MapPin size={10} /> {s.city}</span>}
          <span className="ml-auto inline-flex items-center gap-0.5 text-black font-semibold">{CTA[s.type]} <ExternalLink size={10} /></span>
        </div>
      </div>
    </Link>
  );
}

export function ShareToCommunitySheet({ type, id, onClose }) {
  const nav = useNavigate();
  const [mine, setMine] = useState(null);
  const [preview, setPreview] = useState(null);
  const [cid, setCid] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/communities/mine").then(r => { const l = r.data.filter(c => c.my_membership?.status === "active" && c.status === "approved"); setMine(l); if (l[0]) setCid(l[0].id); }).catch(() => setMine([]));
    api.get("/share-targets/lookup", { params: { type, id } }).then(r => setPreview(r.data)).catch(() => setPreview(false));
  }, [type, id]);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/communities/${cid}/posts`, { text, share: { type, id } });
      toast.success("Partilhado na comunidade");
      onClose(); nav(`/comunidade/${cid}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erro ao partilhar"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="share-community-sheet">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-4 pb-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3"><h3 className="font-display font-bold inline-flex items-center gap-2"><Users size={16} /> Partilhar numa comunidade</h3><button onClick={onClose} aria-label="Fechar" data-testid="share-community-close" className="p-1"><X size={16} /></button></div>
        {preview === false && <p className="text-xs text-red-600" data-testid="share-community-unavailable">Este conteúdo não pode ser partilhado.</p>}
        {preview && <ShareCard share={{ snapshot: preview, available: true }} />}
        {mine && mine.length === 0 && <p className="text-sm text-zinc-600 mt-3" data-testid="share-community-none">Ainda não pertences a nenhuma comunidade. <Link to="/comunidade" className="underline" onClick={onClose}>Explorar comunidades</Link></p>}
        {mine && mine.length > 0 && preview && (
          <>
            <label className="block text-xs font-semibold mt-3 mb-1">Comunidade</label>
            <select value={cid} onChange={e => setCid(e.target.value)} data-testid="share-community-select" className="w-full text-sm border border-zinc-200 rounded-xl px-3 py-2 bg-white">
              {mine.map(c => <option key={c.id} value={c.id}>{c.name}{c.city ? ` · ${c.city}` : ""}</option>)}
            </select>
            <textarea value={text} onChange={e => setText(e.target.value)} maxLength={4000} rows={2} placeholder="Diz algo sobre isto (opcional)…" data-testid="share-community-text"
              className="mt-2 w-full text-sm border border-zinc-200 rounded-xl px-3 py-2 resize-none outline-none focus:border-[#c8102e]" />
            <button onClick={submit} disabled={busy || !cid} data-testid="share-community-submit" className="mt-3 w-full py-2.5 rounded-xl bg-[#c8102e] text-white text-sm font-semibold disabled:opacity-40">Publicar na comunidade</button>
          </>
        )}
      </div>
    </div>
  );
}

export function ShareToCommunityButton({ type, id, className = "", testid }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const click = (e) => { e.preventDefault(); e.stopPropagation(); if (!user) { toast.info("Inicia sessão para partilhar numa comunidade"); nav("/login"); return; } setOpen(true); };
  return (
    <>
      <button type="button" onClick={click} aria-label="Partilhar numa comunidade" title="Partilhar numa comunidade" data-testid={testid || `share-community-${type}-${id}`}
        className={`inline-flex items-center gap-1 rounded-full p-1.5 text-zinc-600 hover:text-black transition-colors ${className}`}><Users size={14} /></button>
      {open && <ShareToCommunitySheet type={type} id={id} onClose={() => setOpen(false)} />}
    </>
  );
}
